"use client";

import { PortableText } from "@portabletext/react";
import {
  customRichTextComponent,
  richTextComponent,
} from "@/sanity/sanityPortableText";
import { CTAButton } from "@/shadcn/ui/button";
import { GroqImage } from "@/helpers/Image";
import type { PageAffiliateSchema } from "@/sanity/pages/PageAffiliateTemplate";
import Image from "next/image";
import logo from "@/public/logo-black.svg";
import arrow from "@/public/global/dotted-arrow-green.svg";
import { motion } from "framer-motion";
import { Fragment } from "react";
import Link from "next/link";
import { cn } from "@/lib/utils";
import bg from "@/public/global/bg-art.jpg";
import ChipSerif from "@/components/global/ui/ChipSerif";
import coopBG from "@/public/affiliate/cooperationbg.png";
import { useIsMobile } from "@/hooks/useIsMobile";
import { PageAffiliateWorkshopSchema } from "@/sanity/pages/PageAffiliateWorkshopTemplate";
import checkbox from "@/public/comp_affiliate/checkboxdark.svg";

export default function Hero({
  data,
  className,
}: {
  data: PageAffiliateWorkshopSchema;
  className?: string;
}) {
  // const isMobile = useIsMobile();

  const placesLeft = parseInt(data.heroPlacesLeft) ?? 10;
  const placesTotal = parseInt(data.heroPlacesTotal) ?? 100;

  return (
    <>
      <section
        id="hero-section"
        className={cn(
          "relative overflow-clip px-[2rem] lg:px-[5rem] pt-[2rem] lg:pt-[5rem]",
          className,
        )}
      >
        <div className="relative overflow-clip w-[min(229.25rem,100%)] mx-auto rounded-[2rem] lg:rounded-[5rem] pt-[5rem] pb-[3.25rem] lg:pb-[15rem]">
          <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-b from-[#F6F9F7] to-[#E6F2EB]">
            <Image
              className="absolute left-1/2 -translate-x-1/2 bottom-0 !w-[max(100%,90rem)] !max-w-none !h-full object-cover object-top mix-blend-luminosity opacity-[.1]"
              src={bg}
              quality={100}
              alt="background art"
            />
          </div>

          <div className="container flex relative z-10 flex-col items-center px-[2.5rem]">
            <div className="w-[min(166.25rem,100%)] flex flex-col lg:flex-row items-center justify-start gap-[2rem]">
              <motion.div
                className="flex justify-center items-center space-x-[1rem] w-full lg:w-fit"
                initial={{ opacity: 0, filter: "blur(10px)" }}
                animate={{ opacity: 1, filter: "blur(0px)" }}
                transition={{
                  duration: 0.3,
                  delay: 0,
                  ease: [0.25, 0.4, 0.25, 1],
                }}
              >
                <Link href={"/"} className="shrink-0">
                  <Image src={logo} alt="" className="w-[15rem]" />
                </Link>

                {data?.heroWorkshopTitle && (
                  <Fragment>
                    <Image
                      src={arrow}
                      alt=""
                      className="shrink-0 !w-[3.182rem]"
                    />
                    <div className="flex items-center gap-[1rem]">
                      <p className="text-[#103037] text-[2rem] font-[400] leading-[116%] m-0 lg:w-auto">
                        {data?.heroWorkshopTitle}
                      </p>
                    </div>
                  </Fragment>
                )}
              </motion.div>
            </div>
            <div className="flex flex-col justify-between items-center w-[min(166.25rem,100%)] lg:flex-row mt-[2rem] lg:mt-[7.5rem]">
              <motion.div
                className="w-[min(73rem,100%)] mb-[4.5rem] lg:mb-0"
                initial={{ opacity: 0, x: -50, filter: "blur(10px)" }}
                animate={{ opacity: 1, x: 0, filter: "blur(0px)" }}
                transition={{
                  duration: 0.3,
                  delay: 0.05,
                  ease: [0.25, 0.4, 0.25, 1],
                }}
              >
                <div>
                  {data?.heroText && (
                    <div className="richtext lg:richtext-start prose-h1:text-[#2E3436] prose-h1:text-[5rem] lg:prose-h1:text-[6.75rem] prose-h1:leading-[130%] prose-h1:tracking-[-0.2025rem] mb-[2.25rem] lg:mb-[1.5rem]">
                      <PortableText
                        value={data.heroText}
                        components={richTextComponent}
                      />
                    </div>
                  )}

                  {data?.heroContent && (
                    <div className="richtext richtext-center lg:richtext-start prose-p:text-[2rem] prose-p:leading-[150%] prose-p:font-[500] prose-strong:font-[400] prose-strong:text-black prose-p:text-[#103037]">
                      <PortableText
                        value={data.heroContent}
                        components={richTextComponent}
                      />
                    </div>
                  )}

                  <hr className="hidden lg:block w-full border-t border-black/10 my-[2.25rem]" />

                  <div className="hidden lg:flex flex-col items-start gap-[2.25rem]">
                    {data.heroCheckList &&
                      data.heroCheckList.length > 0 &&
                      data.heroCheckList.map((item, index) => (
                        <div
                          key={"herocheck" + index}
                          className="flex gap-[1.5rem] items-center"
                        >
                          <Image
                            src={checkbox}
                            alt=""
                            className="!w-[3rem] shrink-0"
                          />
                          <div className="richtext richtext-start prose-p:text-[1.75rem] prose-p:leading-[150%] prose-p:font-[400] prose-strong:font-[400] prose-p:text-[#103037]/70">
                            <PortableText
                              value={item.text}
                              components={richTextComponent}
                            />
                          </div>
                        </div>
                      ))}
                  </div>
                </div>

                <div className="hidden w-full lg:flex flex-col lg:flex-row gap-[2rem] items-center justify-between mt-[4.5rem]">
                  <CTAButton data={data.heroCTA} className="w-full" />
                  <div className="flex flex-col items-center lg:items-start gap-[1.35rem] min-w-[30rem] w-full lg:w-fit">
                    <div className="w-full rounded-full h-[0.875rem] bg-[#103037]/10 overflow-hidden">
                      <div
                        className="h-full bg-[#6DA991] rounded-full"
                        style={{
                          width: 100 - (placesLeft / placesTotal) * 100 + "%",
                        }}
                      />
                    </div>
                    <span className="text-[1.5rem] text-[#103037]/60">
                      Nur noch{" "}
                      <strong className="text-[#103037]">
                        {placesLeft} von {placesTotal}
                      </strong>{" "}
                      Plätzen verfügbar
                    </span>
                  </div>
                </div>
              </motion.div>
              <div className="w-[min(78rem,100%)] relative flex flex-col gap-[2rem]">
                <div className="grid grid-cols-2 gap-[2rem]">
                  {data.heroAffiliate1.image && (
                    <motion.div
                      initial={{ opacity: 0, x: -120, filter: "blur(10px)" }}
                      animate={{
                        opacity: 1,
                        x: 0,
                        filter: "blur(0px)",
                      }}
                      transition={{
                        duration: 0.3,
                        delay: 0.35,
                        ease: [0.25, 0.4, 0.25, 1],
                      }}
                      className="relative rounded-[1.5rem] overflow-hidden"
                    >
                      <Image
                        src={data.heroAffiliate1.image}
                        alt=""
                        quality={100}
                        className=""
                      />
                      {data.heroAffiliate1.name && (
                        <div
                          className="absolute z-[5] left-[1.5rem] top-[1.5rem] backdrop-blur-[7.25px] bg-white/10 rounded-[1rem] px-[2.62rem] py-[0.75rem]
                          text-white font-gestura text-[2.25rem] italic"
                        >
                          {data.heroAffiliate1.name}
                        </div>
                      )}
                    </motion.div>
                  )}

                  {data.heroAffiliate2.image && (
                    <motion.div
                      initial={{ opacity: 0, x: 120, filter: "blur(10px)" }}
                      animate={{
                        opacity: 1,
                        x: 0,
                        filter: "blur(0px)",
                      }}
                      transition={{
                        duration: 0.3,
                        delay: 0.4,
                        ease: [0.25, 0.4, 0.25, 1],
                      }}
                      className="relative rounded-[1.5rem] overflow-hidden"
                    >
                      <Image
                        src={data.heroAffiliate2.image}
                        alt=""
                        quality={100}
                        className=""
                      />
                      {data.heroAffiliate2.name && (
                        <div
                          className="absolute z-[5] left-[1.5rem] top-[1.5rem] backdrop-blur-[7.25px] bg-white/10 rounded-[1rem] px-[2.62rem] py-[0.75rem]
                          text-white font-gestura text-[2.25rem] italic"
                        >
                          {data.heroAffiliate2.name}
                        </div>
                      )}
                    </motion.div>
                  )}
                </div>

                <div className="flex justify-center lg:justify-between bg-[#F0F6F2] border border-[#1030373B] p-[2rem] lg:p-[2.25rem] rounded-[1.5rem]">
                  <span className="hidden lg:block text-[#103037]/60 text-[2rem]">
                    Date
                  </span>
                  <div className="flex items-center gap-[2.25rem]">
                    <span className="text-[#103037]/60 text-[2rem]">
                      {data.dates.start}
                    </span>
                    <div className="bg-[#324E53] shrink-0 w-[0.75rem] h-[0.75rem]"></div>
                    <span className="text-[#103037] text-[2rem]">
                      {data.dates.end}
                    </span>
                  </div>
                </div>

                <div className="lg:hidden w-full flex flex-col lg:flex-row gap-[2rem] items-center justify-between">
                  <CTAButton data={data.heroCTA} className="w-full" />
                  <div className="flex flex-col items-center lg:items-start gap-[1.35rem] max-w-[36rem] lg:max-w-none min-w-[30rem] w-full lg:w-fit">
                    <div className="w-full rounded-full h-[0.875rem] bg-[#103037]/10 overflow-hidden">
                      <div
                        className="h-full bg-[#6DA991] rounded-full"
                        style={{
                          width: 100 - (placesLeft / placesTotal) * 100 + "%",
                        }}
                      />
                    </div>
                    <span className="text-[1.5rem] text-[#103037]/60">
                      Nur noch{" "}
                      <strong className="text-[#103037]">
                        {placesLeft} von {placesTotal}
                      </strong>{" "}
                      Plätzen verfügbar
                    </span>
                  </div>
                </div>

                <div className="lg:hidden flex flex-col items-start gap-[2.25rem] mt-[3rem]">
                  {data.heroCheckList &&
                    data.heroCheckList.length > 0 &&
                    data.heroCheckList.map((item, index) => (
                      <div
                        key={"herocheck" + index}
                        className="flex gap-[1.5rem] items-center"
                      >
                        <Image
                          src={checkbox}
                          alt=""
                          className="!w-[3rem] shrink-0"
                        />
                        <div className="richtext richtext-start prose-p:text-[1.75rem] prose-p:leading-[150%] prose-p:font-[400] prose-strong:font-[400] prose-p:text-[#103037]/70">
                          <PortableText
                            value={item.text}
                            components={richTextComponent}
                          />
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
